import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { API_URL } from '../config';
import { t } from '../i18n';
import './Register.css';

// Список ключів полів, ОБОВ'ЯЗКОВИХ для заповнення. Використовується у трьох місцях:
// fieldError() (підсвічує помилку під конкретним полем після touch), isFormValid()
// (фінальна перевірка перед відправкою) і handleSubmit() (примусово "чіпає" всі поля
// одразу, щоб показати всі помилки, якщо форму намагаються відправити порожньою).
// ⚠️ ТЗ 2026-10-12: "category" (поле 50 RDR, довідник 50.mnu) тепер обов'язкове —
// замінило вільне поле "Посада". "birthYear" (поле 21) НЕ обов'язкове — замінило
// "Країна", лишилось таким же необов'язковим полем, яким та була.
const REG_REQUIRED = ['lastName', 'firstName', 'patronymic', 'education', 'category', 'phone', 'email', 'login', 'password'];
// Проста перевірка формату e-mail (looks-like-email, без повного RFC-парсера).
// Викликається у fieldError() (під час вводу/blur) і в isFormValid() (перед відправкою).
const isEmailValid = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
// Перевірка формату телефону: допускає +, цифри, пробіли, дужки, дефіси.
// ⚠️ Сесія 2026-10-27: лише українські номери (+380 і 9 цифр) — захист від
// накрутки SMS на закордонні номери; сервер перевіряє так само (phone_not_ua).
// Нормалізація — те саме правило, що smsFlyNormalizePhone() у sms_sender.php.
const toUaPhoneDigits = v => {
  let d = v.replace(/\D/g, '');
  if (d.length === 10 && d[0] === '0') d = '38' + d;
  else if (d.length === 9) d = '380' + d;
  else if (d.length === 11 && d.startsWith('80')) d = '3' + d;
  return d;
};
const isPhoneValid = v => /^\+?[\d\s()-]+$/.test(v) && /^380\d{9}$/.test(toUaPhoneDigits(v));
// Поле телефону стартує з префіксом "+38" (крок 4 плану 2026-10-26) — підказка
// формату. Сам префікс без номера вважається порожнім полем (і на сервері —
// register.php, validateForm).
const PHONE_PREFIX = '+38';
const isPhoneBlank = v => v.replace(/\D/g, '') === '' || v.trim() === PHONE_PREFIX;
// Логін і пароль — ті самі правила, що REG_LOGIN_RE/REG_PASSWORD_MIN у
// register.php (крок 1 плану 2026-10-26; сервер перевіряє повторно).
const isLoginValid = v => /^[A-Za-z0-9._-]{3,30}$/.test(v);
const REG_PASSWORD_MIN = 6;
const REG_PASSWORD_MAX = 64;
// Максимальна довжина полів = розміри колонок registration_log на проді
// (register.php, REG_FIELD_MAX) — задовге значення там губить запис журналу.
const REG_MAX_LEN = {
  lastName: 100, firstName: 100, patronymic: 100, workplace: 255,
  phone: 30, email: 255, login: 30, password: REG_PASSWORD_MAX,
};
// Рік народження (поле 21 RDR) — необов'язкове поле, але якщо заповнене, має бути
// реальним 4-значним роком (не довільним числом/сміттям). Перевіряється і тут
// (клієнт), і повторно на сервері (register.php, validateForm()) — не довіряємо клієнту.
const isBirthYearValid = v => {
  if (v === '') return true; // необов'язкове — порожнє завжди валідне
  if (!/^\d{4}$/.test(v)) return false;
  const y = Number(v);
  return y >= 1900 && y <= new Date().getFullYear();
};

// ── Поле "Освіта" (поле 20 RDR) — підписи у формі НАВМИСНО коротші за самі
// значення довідника 20.mnu (пряме побажання бібліотеки, ТЗ 2026-10-12): у
// MARC-запис читача завжди пишеться ЛІВА колонка (справжнє значення словника,
// те саме, що обрав би бібліотекар у Каталогізаторі) — права лише для показу.
// Якщо довідник колись зміниться на боці ІРБІС і конкретне значення не
// знайдеться в цій мапі — компонент просто покаже "пояснення" з самого
// словника (бекенд його й так віддає), нічого не зламається.
const EDUCATION_DISPLAY_LABELS = {
  'Середня освіта': 'Середня',
  'Передвища освіта': 'Передвища',
  'Вища освіта (1-й рівнь)': 'Вища (бакалавр)',
  'Вища освіта (2-й рівнь)': 'Вища (магістр)',
  'Вища освіта (3-й рівнь)': 'Вища (докторський ступінь)',
};

// ── Задачка для браузера (захист від ботів, сесія 2026-10-27; сервер —
// reg_antibot.php). Знайти число n від 0 до max, для якого
// SHA-256(salt + n) === challenge. Людина цього не помічає (частки секунди —
// кілька секунд), а боту кожна спроба реєстрації коштує часу процесора.
// crypto.subtle є лише на https і localhost; на http-адресі в локальній мережі
// (тест-стенд) — запасний варіант на чистому JS (sha256Hex нижче).
async function solvePow(salt, challenge, max) {
  const subtle = window.crypto && window.crypto.subtle;
  const enc = new TextEncoder();
  const BATCH = 500;
  for (let start = 0; start <= max; start += BATCH) {
    const end = Math.min(start + BATCH - 1, max);
    if (subtle) {
      const nums = [];
      for (let n = start; n <= end; n++) nums.push(n);
      const hashes = await Promise.all(nums.map(n => subtle.digest('SHA-256', enc.encode(salt + n))));
      for (let i = 0; i < hashes.length; i++) {
        if (bytesToHex(new Uint8Array(hashes[i])) === challenge) return nums[i];
      }
    } else {
      for (let n = start; n <= end; n++) {
        if (sha256Hex(salt + n) === challenge) return n;
      }
      await new Promise(r => setTimeout(r, 0)); // не "заморожувати" сторінку
    }
  }
  return -1;
}
const bytesToHex = b => Array.from(b, x => x.toString(16).padStart(2, '0')).join('');

// SHA-256 для ASCII-рядка (сіль у hex + цифри) — лише запасний варіант без crypto.subtle.
const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];
function sha256Hex(str) {
  const bytes = [];
  for (let i = 0; i < str.length; i++) bytes.push(str.charCodeAt(i) & 0xff);
  const bitLen = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  for (let i = 7; i >= 0; i--) bytes.push(i >= 4 ? 0 : (bitLen >>> (i * 8)) & 0xff);
  const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const w = new Array(64);
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < bytes.length; off += 64) {
    for (let i = 0; i < 16; i++) {
      w[i] = (bytes[off + i * 4] << 24) | (bytes[off + i * 4 + 1] << 16) | (bytes[off + i * 4 + 2] << 8) | bytes[off + i * 4 + 3];
    }
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + SHA256_K[i] + w[i]) | 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
      hh = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    h[0] = (h[0] + a) | 0; h[1] = (h[1] + b) | 0; h[2] = (h[2] + c) | 0; h[3] = (h[3] + d) | 0;
    h[4] = (h[4] + e) | 0; h[5] = (h[5] + f) | 0; h[6] = (h[6] + g) | 0; h[7] = (h[7] + hh) | 0;
  }
  return h.map(x => (x >>> 0).toString(16).padStart(8, '0')).join('');
}

// POST до register.php з межею очікування (крок 9 плану 2026-10-26): раніше
// fetch міг чекати безкінечно — спінер "крутився вічно", якщо сервер завис.
// Після тайм-ауту — звичайний виняток, його ловлять catch-гілки обробників
// (повтор безпечний: сервер упізнає вже завершену заявку й поверне ту саму відповідь).
const REG_FETCH_TIMEOUT_MS = 60000;
async function postJson(url, body) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REG_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// RegField — НАЙБІЛЬШ ВИКОРИСТОВУВАНИЙ компонент цього файлу: одне поле анкети
// (підпис + іконка + інпут + підказка/помилка). Викликається 10 разів нижче —
// по разу на кожне поле реєстраційної форми, тому вся розмітка й стилі поля
// написані ОДИН раз тут, а не дублюються під кожне окреме поле.
//
// Пропси:
//   id           — html id інпута; потрібен для <label htmlFor> і aria-describedby
//   label        — підпис над полем (рядок уже перекладений через t(lang, ключ))
//   value/onChange — контрольоване значення; onChange завжди приходить від setField(key)
//                  з RegisterForm нижче (керує єдиним стейтом form)
//   error        — вже готовий текст помилки (рахує RegisterForm через fieldError());
//                  не порожній рядок → рамка поля підсвічується червоним (reg-field--error)
//   icon         — назва іконки Font Awesome без префіксу "fa-solid" (напр. "fa-user")
//   required     — чи показувати червону зірочку "*" біля підпису
//   type         — тип <input> (text/tel/email) — впливає на мобільну клавіатуру
//   hint         — необов'язкова сіра підказка під полем (напр. про студентський квиток)
//   autoComplete — html-автозаповнення браузера (family-name/given-name/tel/email тощо)
// ═══════════════════════════════════════════════════════════════════════════
function RegField({ id, label, value, onChange, error, icon, required, type = 'text', hint, autoComplete = 'off', maxLength, inputMode }) {
  const errId = `${id}-err`;
  return (
    <div className={`reg-field${error ? ' reg-field--error' : ''}`}>
      <label className="reg-label" htmlFor={id}>
        {label}{required && <span className="reg-required-mark">*</span>}
      </label>
      <div className="reg-igroup">
        <span className="reg-ipfx"><i className={`fa-solid ${icon}`} /></span>
        <input
          id={id}
          className="reg-input"
          type={type}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          maxLength={maxLength}
          inputMode={inputMode}
          aria-invalid={!!error}
          aria-describedby={error ? errId : undefined}
        />
      </div>
      {hint && !error && <div className="reg-hint">{hint}</div>}
      {error && <div className="reg-field-error" id={errId}>{error}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// RegSelect — випадаючий список для полів, прив'язаних до довідника ІРБІС
// (ТЗ 2026-10-12: поле 50 "Категорія" і поле 20 "Освіта" — обидва мають
// суворо фіксований набір значень, вільний текст більше не допускається).
// Та сама зовнішня "оболонка" поля, що й у RegField (reg-field/reg-label/
// reg-igroup/reg-ipfx) — щоб новий тип поля виглядав ОДНАКОВО зі звичайними
// текстовими, лише сам інпут замінений на кнопку з випадаючим списком.
//
// options — масив {value, label}; value завжди піде прямим текстом у
// MARC-поле читача (повинно 1:1 збігатись із тим, що реально лежить у
// довіднику ІРБІС), label — те, що бачить читач (може відрізнятись від
// value, якщо викликач хоче коротший/дружніший підпис).
//
// Розгортання — ГРАДІЄНТНИЙ прийом grid-template-rows (0fr→1fr) замість
// max-height у px: не потребує заміряти висоту списку в JS і працює для
// будь-якої кількості опцій "із коробки" (той самий клас прийому, що вже
// застосований для акордеонів бокової панелі каталогу, style.md). Список
// розгортається В ПОТОЦІ документа (не position:absolute) — та сама
// причина, що й у DisciplineAutocomplete (App.js): обгортка картки
// реєстрації має власний внутрішній скрол, floating-список міг би
// обрізатись.
// ═══════════════════════════════════════════════════════════════════════════
function RegSelect({ id, label, value, onChange, error, icon, required, options, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const errId = `${id}-err`;

  // Закриття по кліку поза полем або по Escape — підписка лише поки список
  // реально відкритий (той самий економний патерн, що DisciplineAutocomplete).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find(o => o.value === value) || null;

  return (
    <div className={`reg-field${error ? ' reg-field--error' : ''}`} ref={rootRef}>
      <label className="reg-label" htmlFor={id}>
        {label}{required && <span className="reg-required-mark">*</span>}
      </label>
      <div className="reg-igroup">
        <span className="reg-ipfx"><i className={`fa-solid ${icon}`} /></span>
        <button
          type="button"
          id={id}
          className={`reg-input reg-select-trigger${!selected ? ' reg-select-trigger--placeholder' : ''}`}
          onClick={() => setOpen(o => !o)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-describedby={error ? errId : undefined}
        >
          <span className="reg-select-trigger-text">{selected ? selected.label : placeholder}</span>
          <i className={`fa-solid fa-chevron-down reg-select-chevron${open ? ' reg-select-chevron--open' : ''}`} />
        </button>
      </div>
      <div className={`reg-select-list-wrap${open ? ' reg-select-list-wrap--open' : ''}`}>
        <ul className="reg-select-list" role="listbox">
          {options.map(o => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              className={`reg-select-option${o.value === value ? ' reg-select-option--active' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </li>
          ))}
        </ul>
      </div>
      {error && <div className="reg-field-error" id={errId}>{error}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ФОРМА РЕЄСТРАЦІЇ
// ═══════════════════════════════════════════════════════════
function RegisterForm({ lang, onBack, onCancel, onLogin }) {
  // form — ЄДИНИЙ об'єкт зі значеннями всіх 10 полів анкети (керована форма).
  // Ключі збігаються з тими, що в REG_REQUIRED, і саме в такому вигляді
  // (без перетворень) підуть у тіло POST-запиту в handleSubmit(). ⚠️ ТЗ
  // 2026-10-12: "country" → "birthYear" (поле 21, необов'язкове), "position" →
  // "category" (поле 50, обов'язкове, з довідника — див. RegSelect вище).
  const [form, setForm] = useState({
    lastName: '', firstName: '', patronymic: '', birthYear: '',
    education: '', workplace: '', category: '',
    phone: PHONE_PREFIX, email: '', login: '', password: '',
  });

  // ── Довідники ІРБІС для полів "Категорія"/"Освіта" (ТЗ 2026-10-12) —
  // завантажуються ОДИН раз при монтуванні форми, живим запитом до RDR
  // (register.php?action=dictionaries, config.php readRdrDictionaryPairs() —
  // ЛИШЕ протокол, без локального файлу, детально — backend.md). Поки
  // dictLoading===true обидва RegSelect показують порожній список (кнопка
  // "Зареєструватися" при цьому все одно заблокована через isFormValid(),
  // бо category/education обов'язкові й ще не можуть бути обрані).
  const [dictionaries, setDictionaries] = useState({ categories: [], education: [] });
  const [dictLoading, setDictLoading] = useState(true);
  const [dictError, setDictError] = useState('');

  const loadDictionaries = () => {
    setDictLoading(true);
    setDictError('');
    fetch(`${API_URL}/register.php?action=dictionaries`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setDictionaries({ categories: data.categories || [], education: data.education || [] });
        } else {
          setDictError(t(lang, 'reg_dict_err'));
        }
      })
      .catch(() => setDictError(t(lang, 'reg_dict_err')))
      .finally(() => setDictLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDictionaries(); }, []);

  // ── Захист від ботів (сесія 2026-10-27, сервер — reg_antibot.php):
  // картинка-капча + задачка для браузера + поле-пастка. Капча одноразова —
  // нова береться при відкритті форми і після КОЖНОЇ спроби action=start.
  const [captcha, setCaptcha] = useState(null);       // {token, salt, challenge, maxnumber, image}
  const [captchaLoading, setCaptchaLoading] = useState(true);
  const [captchaValue, setCaptchaValue] = useState(''); // цифри, які ввів читач
  const [captchaError, setCaptchaError] = useState('');
  const [hpValue, setHpValue] = useState('');           // поле-пастка: людина його не бачить
  const [checkingBot, setCheckingBot] = useState(false); // браузер розв'язує задачку

  const loadCaptcha = () => {
    setCaptchaLoading(true);
    fetch(`${API_URL}/register.php?action=challenge`, { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setCaptcha(data);
        } else {
          setCaptcha(null);
          setCaptchaError(t(lang, data.error === 'registration_paused' ? 'reg_err_paused' : 'reg_err_captcha_unavailable'));
        }
      })
      .catch(() => { setCaptcha(null); setCaptchaError(t(lang, 'reg_err_captcha_unavailable')); })
      .finally(() => setCaptchaLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCaptcha(); }, []);
  const refreshCaptcha = () => { setCaptchaValue(''); setCaptchaError(''); loadCaptcha(); };

  // Готові options[] для RegSelect — category показує саме те значення, що
  // піде в MARC (своєї "гарної" мітки для нього не просили); education
  // підміняє підпис через EDUCATION_DISPLAY_LABELS (коротший текст за
  // прямим побажанням бібліотеки), value лишається незмінним значенням
  // довідника 20.mnu.
  const categoryOptions = dictionaries.categories.map(c => ({ value: c, label: c }));
  const educationOptions = dictionaries.education.map(e => ({
    value: e.value,
    label: EDUCATION_DISPLAY_LABELS[e.value] || e.label || e.value,
  }));
  // touched — які поля вже треба перевіряти. НЕ виставляється по одному при
  // втраті фокусу (раніше було так — поле червоніло, щойно клацнути повз нього,
  // навіть без спроби щось увести) — тепер виставляється одразу для ВСІХ полів
  // разом, і тільки в handleSubmit(), при першій спробі відправити форму.
  // До цього моменту жодне поле не підсвічується, скільки по них не клацай.
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false); // йде запит до register.php — блокує кнопку, показує спінер
  const [serverError, setServerError] = useState('');  // текст помилки від сервера/мережі → показується в .reg-alert зверху форми
  const [success, setSuccess] = useState(false);       // заявку прийнято → перемикає компонент на екран-подяку (нижче, if (success))
// confirmPassword — НЕ частина form: лише звіряється з form.password на клієнті,
  // у тілі POST-запиту на register.php (JSON.stringify(form)) не бере участі.
  const [confirmPassword, setConfirmPassword] = useState('');

  // Автоматичний вхід одразу з екрана "Заявку прийнято" — щоб не змушувати читача
  // повторно вводити щойно придумані логін/пароль у формі входу. form.login/
  // form.password тут ще ті самі, з реєстраційної форми (стан не скидається).
  const [autoLoginLoading, setAutoLoginLoading] = useState(false);
  const [autoLoginError,   setAutoLoginError]   = useState('');

  // ── OTP-підтвердження телефону (⚠️ тимчасовий симулятор — Registration.md,
  // розділ 6: справжнього SMS/Telegram-каналу ще немає, код повертається
  // прямо у відповіді register.php?action=start/resend, otpDebugCode нижче
  // показує його читачеві просто для перевірки роботи бекенда) ──
  const [otpOpen, setOtpOpen] = useState(false);       // модалка змонтована
  // Модалку закрили хрестиком, не завершивши (крок 3 плану 2026-10-26) —
  // у формі з'являється кнопка повернутись до тієї ж заявки без нової SMS.
  // Скидається, щойно читач змінить хоч одне поле: тоді стара заявка вже
  // не відповідає анкеті на екрані.
  const [otpPaused, setOtpPaused] = useState(false);
  // 'code' | 'alreadyRegisteredConfirm' | 'candidates' — 'alreadyRegisteredConfirm'
  // (редизайн 2026-10-13) — екран "можливо, у вас вже є акаунт, це ви?", приходить
  // з action=verify (needsAlreadyRegisteredConfirm), коли знайдено вже зареєстрований
  // запис з тим самим ПІБ, але звірити телефоном/роком народження нічим.
  const [otpStage, setOtpStage] = useState('code');
  const [requestId, setRequestId] = useState('');
  const [otpDebugCode, setOtpDebugCode] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpResends, setOtpResends] = useState(0);
  // Скільки секунд ще чекати до "Надіслати ще раз" (крок 2 плану 2026-10-26).
  // Значення паузи приходить із сервера (resend_after/retry_after) — сервер
  // і сам не дасть відправити раніше, тут лише показ відліку на кнопці.
  const [resendWait, setResendWait] = useState(0);
  // Кандидати-тезки серед уже внесених бібліотекою записів студентів
  // (Registration.md, розділ 8) — приходять з action=verify, коли збігів
  // за ПІБ кілька і система не може обрати сама.
  const [candidates, setCandidates] = useState([]);
  // Стадія 'retry' (крок 6 плану 2026-10-26): фінальний запис не вдався, але
  // заявка жива — повтор без нової SMS (register.php?action=retryFinal).
  // retryError — 'login_taken' (треба новий логін) або збій зв'язку/запису.
  const [retryError, setRetryError] = useState('');
  const [retryLogin, setRetryLogin] = useState('');
  // needs_manual_review — окремий термінальний екран (не success, не помилка
  // форми): бібліотекар отримає заявку на ручну перевірку.
  const [reviewNeeded, setReviewNeeded] = useState(false);

  // Жива перевірка зайнятості логіна під час набору (Registration.md, розділ 4 —
  // раніше відкрите питання). status: 'idle' | 'checking' | 'available' | 'taken';
  // checkedLogin — саме те значення, до якого відноситься status (щоб не показати
  // застарілий результат, поки читач вже набирає щось інше).
  const [loginCheck, setLoginCheck] = useState({ status: 'idle', checkedLogin: '' });

  // setField(key) — фабрика onChange-обробників: замість окремої функції на
  // кожне з 10 полів один генератор. setField('email') поверне готовий
  // обробник саме для поля email, який пише в form.email.
  const setField = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  // Розбір кодів помилок від register.php (сервер валідує ще раз, не довіряє
  // клієнту) — коди типу "invalid_email"/"invalid_phone" мапляться на вже
  // наявні тексти помилок полів, решта — на загальний reg_err_conn.
  const mapStartError = err => {
    if (err === 'invalid_email') return t(lang, 'reg_err_email');
    if (err === 'invalid_phone') return t(lang, 'reg_err_phone');
    if (err === 'invalid_birth_year') return t(lang, 'reg_err_birth_year');
    // invalid_category/invalid_education — захист сервера про всяк випадок
    // (клієнт і так пропонує обирати лише зі списку, тому ці коди на
    // практиці майже ніколи не повинні приходити — детально register.php).
    if (err === 'invalid_category' || err === 'invalid_education') return t(lang, 'reg_err_invalid_field');
    if (err === 'already_registered') return t(lang, 'reg_err_already_registered');
    if (err === 'rate_limited') return t(lang, 'reg_err_rate_limited');
    if (err === 'invalid_login') return t(lang, 'reg_err_login_format');
    if (err === 'password_too_short') return t(lang, 'reg_err_password_short');
    if (err === 'too_long') return t(lang, 'reg_err_too_long');
    if (err === 'sms_send_failed') return t(lang, 'reg_err_sms_failed');
    if (err === 'missing_phone') return t(lang, 'reg_err_phone');
    if (err === 'irbis_unavailable') return t(lang, 'reg_err_irbis_unavailable');
    if (err === 'phone_not_ua') return t(lang, 'reg_err_phone_ua');
    if (err === 'sms_quota') return t(lang, 'reg_err_sms_quota');
    if (err === 'registration_paused') return t(lang, 'reg_err_paused');
    return t(lang, 'reg_err_conn');
  };
  // Помилки капчі показуються під самою капчею, а не вгорі форми.
  const mapCaptchaError = err => {
    if (err === 'captcha_expired') return t(lang, 'reg_err_captcha_expired');
    if (err === 'captcha_invalid' || err === 'captcha_required') return t(lang, 'reg_err_captcha_invalid');
    return '';
  };
  const mapFinalError = err => {
    if (err === 'login_taken') return t(lang, 'reg_err_login_taken');
    if (err === 'already_registered') return t(lang, 'reg_err_already_registered');
    if (err === 'connection_failed' || err === 'write_failed') return t(lang, 'reg_err_write_failed');
    if (err === 'expired') return t(lang, 'reg_otp_err_expired');
    return t(lang, 'reg_err_conn');
  };

  // fieldError(key) — рахує текст помилки для ОДНОГО поля; викликається
  // в кожному <RegField error={fieldError('...')} .../> нижче (10 разів).
  // Порядок перевірок: 1) поки поле не touched — помилки не показуємо;
  // 2) якщо поле в REG_REQUIRED і порожнє — "Обов'язкове поле";
  // 3) додатково для email — ще й перевірка формату через isEmailValid.
  const fieldError = key => {
    if (!touched[key]) return '';
    // confirmPassword лежить поза form — окрема гілка перевірки: обов'язкове
    // поле + звірка з уже введеним паролем.
    if (key === 'confirmPassword') {
      const v = confirmPassword.trim();
      if (!v) return t(lang, 'reg_err_required');
      if (v !== form.password.trim()) return t(lang, 'reg_err_password_mismatch');
      return '';
    }
    if (key === 'captcha') return captchaValue.length === 5 ? '' : t(lang, 'reg_err_captcha_required');
    const v = (form[key] || '').trim();
    if (REG_REQUIRED.includes(key) && !v) return t(lang, 'reg_err_required');
    if (key === 'phone' && isPhoneBlank(v)) return t(lang, 'reg_err_required');
    if (key === 'email' && v && !isEmailValid(v)) return t(lang, 'reg_err_email');
    if (key === 'phone' && v && !isPhoneValid(v)) return t(lang, 'reg_err_phone');
    if (key === 'login' && v && !isLoginValid(v)) return t(lang, 'reg_err_login_format');
    if (key === 'password' && v && v.length < REG_PASSWORD_MIN) return t(lang, 'reg_err_password_short');
    // birthYear — НЕ в REG_REQUIRED (необов'язкове поле), тому перевірка
    // формату тут окрема: порожнє значення ніколи не помилка, непорожнє —
    // має бути реальним 4-значним роком (isBirthYearValid вище).
    if (key === 'birthYear' && v && !isBirthYearValid(v)) return t(lang, 'reg_err_birth_year');
    return '';
  };

  // isFormValid() — фінальна перевірка ПЕРЕД відправкою (незалежно від touched):
  // усі обов'язкові поля заповнені і email коректний. Викликається один раз
  // на самому початку handleSubmit(), одразу після примусового touch усіх полів.
  const isFormValid = () => {
    for (const key of REG_REQUIRED) {
      if (!(form[key] || '').trim()) return false;
    }
    if (!isPhoneValid(form.phone.trim())) return false;
    if (!isLoginValid(form.login.trim())) return false;
    if (form.password.trim().length < REG_PASSWORD_MIN) return false;
    if (!isBirthYearValid(form.birthYear.trim())) return false; // необов'язкове, але якщо заповнене — формат має бути вірним
    if (confirmPassword.trim() !== form.password.trim()) return false;
    if (captchaValue.length !== 5) return false;
    return isEmailValid(form.email.trim());
  };

  // handleSubmit — обробник onSubmit форми (<form className="reg-card" onSubmit={handleSubmit}>).
  //  1) примусово позначає ВСІ поля touched — якщо натиснути "Записатися" одразу,
  //     не заповнивши нічого, помилки з'являться під усіма обов'язковими полями одразу;
  //  2) якщо isFormValid() не пройшла — далі не йдемо (помилки вже видно з п.1);
  //  3) POST на register.php (JSON-тіло = весь об'єкт form як є). Бекенд register.php
  //     ще не реалізований (окреме, свідомо відкладене завдання) — тому зараз при
  //     відсутності відповіді від сервера покаже reg_err_conn;
  //  4) success → компонент перемикається на екран-подяку (див. if (success) нижче).
// handleSubmit тепер лише запускає OTP-цикл (action=start) і відкриває
  // модалку введення коду — сама реєстрація (запис у RDR) відбувається
  // пізніше, після підтвердження коду, у handleOtpSubmit/handleSelectCandidate.
  const handleSubmit = async e => {
    e.preventDefault();
    setTouched({ ...Object.fromEntries(Object.keys(form).map(k => [k, true])), confirmPassword: true, captcha: true });
    if (!isFormValid()) return;

    // Живий індикатор зайнятості логіна (useEffect([form.login]) вище) — якщо вже
    // точно відомо, що логін зайнятий, немає сенсу проганяти цілий OTP-цикл заради
    // відмови в самому кінці. Фінальна перевірка на сервері (register.php,
    // finalizeRegistration()) лишається незалежно від цього — це лише економія кроку.
    if (loginCheck.status === 'taken' && loginCheck.checkedLogin === form.login.trim()) {
      setServerError(t(lang, 'reg_err_login_taken'));
      return;
    }

    if (!captcha) {
      setCaptchaError(t(lang, 'reg_err_captcha_unavailable'));
      loadCaptcha();
      return;
    }

    setSubmitting(true);
    setServerError('');
    setCaptchaError('');
    try {
      // Задачка для браузера (захист від ботів) — перед відправкою анкети.
      setCheckingBot(true);
      const powNumber = await solvePow(captcha.salt, captcha.challenge, captcha.maxnumber);
      setCheckingBot(false);
      if (powNumber < 0) {
        setCaptchaError(t(lang, 'reg_err_captcha_expired'));
        return;
      }
      const data = await postJson(`${API_URL}/register.php?action=start`, {
        ...form,
        captcha_token: captcha.token,
        captcha_answer: captchaValue,
        pow_number: powNumber,
        hp: hpValue,
      });
      const capMsg = data.success ? '' : mapCaptchaError(data.error);
      if (data.success) {
        setRequestId(data.request_id);
        setOtpDebugCode(data.otp_debug_code || '');
        setOtpValue('');
        setOtpError('');
        setOtpResends(0);
        setResendWait(data.resend_after || 60);
        setOtpStage('code');
        setOtpPaused(false);
        setOtpOpen(true);
        // Нова капча нижче порожня — не підсвічувати її червоним за вже успішну спробу.
        setTouched(tch => ({ ...tch, captcha: false }));
      } else if (capMsg) {
        setCaptchaError(capMsg);
      } else {
        setServerError(mapStartError(data.error));
      }
    } catch {
      setServerError(t(lang, 'reg_err_conn'));
    } finally {
      setCheckingBot(false);
      setSubmitting(false);
      // Токен капчі одноразовий — після будь-якої спроби беремо нову картинку.
      setCaptchaValue('');
      loadCaptcha();
    }
  };

  // Спільна обробка "фінального" результату finalizeRegistration() на
  // бекенді — приходить і з action=verify (коли збіг одразу однозначний:
  // 0 чи 1 кандидат), і з action=selectCandidate (після вибору серед тезок).
  const applyFinalResult = data => {
    if (data.success) {
      setOtpOpen(false);
      setOtpPaused(false);
      setSuccess(true);
      return;
    }
    if (data.error === 'needs_manual_review') {
      setOtpOpen(false);
      setOtpPaused(false);
      setReviewNeeded(true);
      return;
    }
    // Крок 6 плану 2026-10-26: login_taken / connection_failed / write_failed
    // більше НЕ скидають читача на початок — заявка на сервері жива (retryable),
    // показуємо екран повтору без нової SMS.
    if (data.retryable) {
      if (data.error === 'invalid_login') {
        setOtpError(t(lang, 'reg_err_login_format'));
        return; // лишаємось на екрані повтору з новим логіном
      }
      setRetryError(data.error);
      if (data.error === 'login_taken') setRetryLogin('');
      setOtpError('');
      setOtpStage('retry');
      return;
    }
    // Тимчасові стани — вікно не закриваємо, читач просто повторює дію.
    if (data.error === 'in_progress') { setOtpError(t(lang, 'reg_err_in_progress')); return; }
    if (data.error === 'irbis_unavailable') { setOtpError(t(lang, 'reg_err_irbis_unavailable')); return; }
    // Остаточні помилки (already_registered, expired тощо) — назад до форми.
    setOtpOpen(false);
    setOtpPaused(false);
    setServerError(mapFinalError(data.error));
  };

  const handleOtpSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();
    if (otpValue.trim().length !== 6) return;
    setOtpSubmitting(true);
    setOtpError('');
    try {
      const data = await postJson(`${API_URL}/register.php?action=verify`, { request_id: requestId, code: otpValue.trim() });
      if (data.success && data.needsAlreadyRegisteredConfirm) {
        setOtpStage('alreadyRegisteredConfirm');
        return;
      }
      if (data.success && data.needsCandidateChoice) {
        setCandidates(data.candidates || []);
        setOtpStage('candidates');
        return;
      }
      if (!data.success && data.error === 'invalid_code') {
        setOtpError(`${t(lang, 'reg_otp_err_invalid')} ${data.attempts_left ?? ''}`);
        return;
      }
      if (!data.success && (data.error === 'expired' || data.error === 'too_many_attempts')) {
        setOtpOpen(false);
        setServerError(data.error === 'too_many_attempts' ? t(lang, 'reg_otp_err_too_many') : t(lang, 'reg_otp_err_expired'));
        return;
      }
      applyFinalResult(data);
    } catch {
      setOtpError(t(lang, 'reg_err_conn'));
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpResends >= 5 || resendWait > 0) return;
    setOtpError('');
    try {
      const data = await postJson(`${API_URL}/register.php?action=resend`, { request_id: requestId });
      if (data.success) {
        setOtpDebugCode(data.otp_debug_code || '');
        setOtpValue('');
        setOtpResends(n => n + 1);
        setResendWait(data.resend_after || 60);
      } else if (data.error === 'resend_cooldown') {
        setResendWait(data.retry_after || 60);
      } else if (data.error === 'sms_send_failed') {
        // Раніше сюди падало в "код прострочено" — вводило в оману: заявка жива,
        // попередній код дійсний, просто нова SMS не пішла (крок 4 плану 2026-10-26).
        setOtpError(t(lang, 'reg_err_sms_failed'));
      } else if (data.error === 'sms_quota' || data.error === 'registration_paused') {
        setOtpError(mapStartError(data.error));
      } else {
        setOtpError(data.error === 'resend_limit' ? t(lang, 'reg_otp_resend_limit') : t(lang, 'reg_otp_err_expired'));
      }
    } catch {
      setOtpError(t(lang, 'reg_err_conn'));
    }
  };

  // Відповідь читача на екран "можливо, у вас вже є акаунт — це ви?"
  // (otpStage==='alreadyRegisteredConfirm', редизайн 2026-10-13). isMe=true →
  // той самий термінальний результат, що впевнений збіг (applyFinalResult
  // покаже reg_err_already_registered); isMe=false → бекенд продовжує логіку
  // кандидатів з тими самими даними, що вже знайшов на кроці verify — може
  // одразу завершитись (success/needs_manual_review) або показати екран
  // вибору кандидатів (той самий needsCandidateChoice, що й у handleOtpSubmit).
  // Мережева помилка/тайм-аут на цих кроках більше НЕ закриває вікно (крок 6
  // плану 2026-10-26): сервер міг устигнути все зробити, а повтор того ж
  // натискання безпечний — він поверне вже готову відповідь.
  const handleConfirmAlreadyRegistered = async isMe => {
    setOtpSubmitting(true);
    setOtpError('');
    try {
      const data = await postJson(`${API_URL}/register.php?action=confirmAlreadyRegistered`, { request_id: requestId, isMe });
      if (data.success && data.needsCandidateChoice) {
        setCandidates(data.candidates || []);
        setOtpStage('candidates');
        return;
      }
      applyFinalResult(data);
    } catch {
      setOtpError(t(lang, 'reg_err_conn'));
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleSelectCandidate = async mfn => {
    setOtpSubmitting(true);
    setOtpError('');
    try {
      const data = await postJson(`${API_URL}/register.php?action=selectCandidate`, { request_id: requestId, mfn });
      applyFinalResult(data);
    } catch {
      setOtpError(t(lang, 'reg_err_conn'));
    } finally {
      setOtpSubmitting(false);
    }
  };

  // Повтор фінального запису (крок 6 плану 2026-10-26) — без нової SMS.
  // Для login_taken надсилаємо новий логін; після успіху він же стає
  // form.login (його використає кнопка автовходу на екрані "Готово").
  const handleRetryFinal = async () => {
    const newLogin = retryError === 'login_taken' ? retryLogin.trim() : '';
    if (retryError === 'login_taken' && !isLoginValid(newLogin)) return;
    setOtpSubmitting(true);
    setOtpError('');
    try {
      const body = newLogin ? { request_id: requestId, login: newLogin } : { request_id: requestId };
      const data = await postJson(`${API_URL}/register.php?action=retryFinal`, body);
      if (data.success && newLogin) setForm(f => ({ ...f, login: newLogin }));
      applyFinalResult(data);
    } catch {
      setOtpError(t(lang, 'reg_err_conn'));
    } finally {
      setOtpSubmitting(false);
    }
  };

  const closeOtpModal = () => { setOtpOpen(false); setOtpPaused(true); };
  const reopenOtpModal = () => { setOtpPaused(false); setOtpOpen(true); };

  // Будь-яка зміна анкети після закриття модалки — стара заявка вже неактуальна.
  useEffect(() => { setOtpPaused(false); }, [form, confirmPassword]);

  // Автовхід із екрана "Заявку прийнято" — той самий запит, що робить doLogin()
  // у UserLogin.js. При успіху викликає onLogin(data) — той самий проп, що
  // LoginPage отримує з App.js (handleLogin); App.js сам перемкне додаток на
  // каталог, RegisterForm/LoginPage розмонтуються автоматично.
  const handleAutoLogin = async () => {
    if (!onLogin) { onBack(); return; }
    setAutoLoginLoading(true);
    setAutoLoginError('');
    try {
      const res = await fetch(`${API_URL}/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: form.login, password: form.password }),
      });
      const data = await res.json();
      if (data.success) onLogin(data);
      else setAutoLoginError(t(lang, 'reg_autologin_err'));
    } catch {
      setAutoLoginError(t(lang, 'reg_autologin_err'));
    } finally {
      setAutoLoginLoading(false);
    }
  };

  // Попередження при закритті вкладки/перезавантаженні з незбереженими даними.
  // Спрацьовує лише на СПРАВЖНЬОМУ виході зі сторінки (закриття/перезавантаження/
  // адресний рядок) — кнопка "назад" картки (onBack) залишається в межах SPA і
  // beforeunload не чіпає. Текст діалогу браузер завжди показує свій стандартний
  // (сучасні браузери ігнорують будь-який кастомний returnValue з міркувань
  // безпеки) — керувати можна лише самим фактом появи попередження.
  useEffect(() => {
    // Префікс "+38" у полі телефону — не введені дані (сам по собі не причина попереджати).
    const hasData = Object.entries(form).some(([k, v]) => (k === 'phone' ? !isPhoneBlank(v || '') : (v || '').trim() !== ''))
      || confirmPassword.trim() !== '';
    if (!hasData || success) return undefined;
    const handler = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [form, confirmPassword, success]);

  // Відлік паузи до повторної SMS — щосекунди мінус одна, поки не 0.
  useEffect(() => {
    if (resendWait <= 0) return undefined;
    const timer = setTimeout(() => setResendWait(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendWait]);

  // Жива перевірка зайнятості логіна — дебаунс 450мс + AbortController, той самий
  // патерн, що DisciplineAutocomplete (App.js), тільки замість списку підказок —
  // один статус (вільний/зайнятий). Мінімум 3 символи — не сенс звертатись до
  // ІРБІС на кожну одну-дві літери. Це ЛИШЕ допоміжна підказка: фінальна перевірка
  // унікальності логіна все одно відбувається на сервері (register.php, момент
  // фактичного запису) — мережева помилка тут просто мовчки прибирає підказку.
  useEffect(() => {
    const login = form.login.trim();
    if (!isLoginValid(login)) {
      setLoginCheck({ status: 'idle', checkedLogin: '' });
      return undefined;
    }
    setLoginCheck({ status: 'checking', checkedLogin: '' });
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/register.php?action=checkLogin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ login }),
          signal: controller.signal,
        });
        const data = await res.json();
        if (data.success && data.available === true) setLoginCheck({ status: 'available', checkedLogin: login });
        else if (data.success && data.available === false) setLoginCheck({ status: 'taken', checkedLogin: login });
        else setLoginCheck({ status: 'idle', checkedLogin: '' });
      } catch {
        setLoginCheck({ status: 'idle', checkedLogin: '' });
      }
    }, 450);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [form.login]);

  // Екран "Заявку прийнято" — рендериться ЗАМІСТЬ форми (замість <form>, звичайний
  // <div>, бо тут уже нічого не вводиться і не відправляється). Той самий клас
  // reg-card, тому фон/картка виглядають так само — крапка входу в UserLogin.js
  // цього не помічає (просто монтується/розмонтовується весь <RegisterForm>).
  // Кнопка нижче використовує ту саму reg-btn-main, що й кнопка відправки форми
  // (той самий клас, інший текст і дія — веде назад на LoginPage через onBack).
if (success) {
    return (
      <div className="reg-card reg-card--success">
        <div className="reg-success-icon"><i className="fa-solid fa-circle-check" /></div>
        <div className="reg-success-title">{t(lang, 'reg_success_title')}</div>
        <div className="reg-success-text">{t(lang, 'reg_success_text')}</div>
        {autoLoginError && (
          <div className="reg-alert">
            <i className="fa-solid fa-circle-exclamation" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{autoLoginError}</span>
          </div>
        )}
        <div className="reg-success-btns">
          <button type="button" className="reg-btn reg-btn-login" onClick={handleAutoLogin} disabled={autoLoginLoading}>
            {autoLoginLoading
              ? <><Spinner size="sm" animation="border" className="me-2" />{t(lang, 'reg_login_now_btn')}</>
              : <><i className="fa-solid fa-arrow-right-to-bracket" /> {t(lang, 'reg_login_now_btn')}</>
            }
          </button>
          <button type="button" className="reg-btn reg-btn-main" onClick={onBack} disabled={autoLoginLoading}>
            <i className="fa-solid fa-arrow-left" /> {t(lang, 'reg_back_to_login')}
          </button>
        </div>
      </div>
    );
  }

  // needs_manual_review (Registration.md, розділ 8, пункт 5) — кілька
  // кандидатів-тезок з однаковим факультетом+групою, автоматичний вибір
  // неможливий; заявка вже пішла бібліотекарю на ручну перевірку.
  if (reviewNeeded) {
    return (
      <div className="reg-card reg-card--success">
        <div className="reg-success-icon"><i className="fa-solid fa-user-clock" /></div>
        <div className="reg-success-title">{t(lang, 'reg_review_title')}</div>
        <div className="reg-success-text">{t(lang, 'reg_review_text')}</div>
        <button type="button" className="reg-btn reg-btn-main" onClick={onBack}>
          <i className="fa-solid fa-arrow-left" /> {t(lang, 'reg_back_to_login')}
        </button>
      </div>
    );
  }

  return (
    <form className="reg-card" onSubmit={handleSubmit} noValidate>
      {/* Кругла кнопка "назад" у лівому верхньому куті картки — onBack прийшов
          пропсом з UserLogin.js (там це goToLogin — плавний перехід назад на вхід).
          onCancel — реєстрацію відкрив гість з каталогу: «назад» веде туди */}
      <button type="button" className="reg-back-btn" onClick={onCancel || onBack}
        aria-label={t(lang, onCancel ? 'back' : 'reg_back_to_login')} title={t(lang, onCancel ? 'back' : 'reg_back_to_login')}>
        <i className="fa-solid fa-arrow-left" />
      </button>

      {/* reg-card-scroll — окремий елемент БЕЗ border-radius, саме на ньому overflow-y:auto
          (сама .reg-card тепер лише декоративна рамка з overflow:hidden, деталі — Register.css) */}
      <div className="reg-card-scroll">
      <div className="reg-card-head">
        <div className="reg-card-icon"><i className="fa-solid fa-user-plus" /></div>
        <div>
          <div className="reg-card-title">{t(lang, 'reg_title')}</div>
        </div>
      </div>

      {serverError && (
        <div className="reg-alert">
          <i className="fa-solid fa-circle-exclamation" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{serverError}</span>
        </div>
      )}

      <div className="reg-required-hint">{t(lang, 'reg_required_hint')}</div>

{/* Особисті дані — перша з 3 секцій форми (reg-section/reg-section-title — теж
          перевикористовуються 3 рази, стилі в Register.css). reg-row--2col нижче —
          2 поля в один ряд від 576px і ширше, одна колонка на вузьких екранах
          (сама логіка колонок — у Register.css, тут лише клас) */}
      <div className="reg-section">
        <div className="reg-section-title">{t(lang, 'reg_section_personal')}</div>
        <div className="reg-row reg-row--2col">
          <RegField id="reg-lastname" label={t(lang, 'reg_lastname')} required icon="fa-user"
            value={form.lastName} onChange={setField('lastName')}
            error={fieldError('lastName')} autoComplete="family-name" maxLength={REG_MAX_LEN.lastName} />
          <RegField id="reg-firstname" label={t(lang, 'reg_firstname')} required icon="fa-user"
            value={form.firstName} onChange={setField('firstName')}
            error={fieldError('firstName')} autoComplete="given-name" maxLength={REG_MAX_LEN.firstName} />
        </div>
        <div className="reg-row reg-row--2col">
          <RegField id="reg-patronymic" label={t(lang, 'reg_patronymic')} required icon="fa-user"
            value={form.patronymic} onChange={setField('patronymic')}
            error={fieldError('patronymic')} autoComplete="additional-name" maxLength={REG_MAX_LEN.patronymic} />
          {/* Рік народження (поле 21 RDR) — замінило "Країну" (ТЗ 2026-10-12).
              Необов'язкове, як і "Країна" раніше; НЕ з довідника ІРБІС (поле
              21 вільне, без прив'язаного .mnu — backend.md) — тому звичайний
              RegField, а не RegSelect. type="text"+inputMode="numeric" замість
              type="number", щоб уникнути стрілочок-лічильника браузера й не
              дозволити ввести крапку/мінус, яких у 4-значному році бути не може. */}
          <RegField id="reg-birthyear" label={t(lang, 'reg_birth_year')} icon="fa-cake-candles"
            type="text" value={form.birthYear}
            onChange={e => setForm(f => ({ ...f, birthYear: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
            error={fieldError('birthYear')} autoComplete="bday-year" />
        </div>
      </div>

      {/* Освіта та категорія */}
      <div className="reg-section">
        <div className="reg-section-title">{t(lang, 'reg_section_edu')}</div>
        {dictError && (
          <div className="reg-alert">
            <i className="fa-solid fa-circle-exclamation" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{dictError} </span>
            <button type="button" className="reg-dict-retry-btn" onClick={loadDictionaries}>
              <i className="fa-solid fa-rotate" /> {t(lang, 'reg_dict_retry')}
            </button>
          </div>
        )}
        <div className="reg-row reg-row--2col">
          {/* Освіта (поле 20 RDR) — тепер довідник 20.mnu замість вільного
              тексту (ТЗ 2026-10-12): educationOptions уже готові вище
              (value — значення словника, label — коротший підпис бібліотеки). */}
          <RegSelect id="reg-education" label={t(lang, 'reg_education')} required icon="fa-graduation-cap"
            value={form.education} onChange={v => setForm(f => ({ ...f, education: v }))}
            error={fieldError('education')} options={educationOptions} disabled={dictLoading}
            placeholder={dictLoading ? t(lang, 'reg_dict_loading') : t(lang, 'reg_education_ph')} />
          <RegField id="reg-workplace" label={t(lang, 'reg_workplace')} icon="fa-building"
            value={form.workplace} onChange={setField('workplace')} autoComplete="organization" maxLength={REG_MAX_LEN.workplace} />
        </div>
        <div className="reg-row">
          {/* Категорія (поле 50 RDR) — замінила "Посаду" (ТЗ 2026-10-12),
              тепер обов'язкове поле з довідника 50.mnu. */}
          <RegSelect id="reg-category" label={t(lang, 'reg_category')} required icon="fa-id-badge"
            value={form.category} onChange={v => setForm(f => ({ ...f, category: v }))}
            error={fieldError('category')} options={categoryOptions} disabled={dictLoading}
            placeholder={dictLoading ? t(lang, 'reg_dict_loading') : t(lang, 'reg_category_ph')} />
        </div>
      </div>

      {/* Контакти */}
      <div className="reg-section">
        <div className="reg-section-title">{t(lang, 'reg_section_contact')}</div>
        <div className="reg-row reg-row--2col">
          <RegField id="reg-phone" label={t(lang, 'reg_phone')} required type="tel" icon="fa-phone"
            value={form.phone} onChange={setField('phone')}
            error={fieldError('phone')} autoComplete="tel" maxLength={REG_MAX_LEN.phone} />
          <RegField id="reg-email" label={t(lang, 'reg_email')} required type="email" icon="fa-envelope"
            value={form.email} onChange={setField('email')}
            error={fieldError('email')} autoComplete="email" maxLength={REG_MAX_LEN.email} />
        </div>
      </div>

      {/* Дані для входу — читач сам придумує логін і пароль (не бібліотекар
          повідомляє їх окремо, як у скасованій архітектурі RDRV) */}
      <div className="reg-section">
        <div className="reg-section-title">{t(lang, 'reg_section_account')}</div>
        <div className="reg-row reg-row--2col">
          <div className="reg-login-field-wrap">
            <RegField id="reg-login" label={t(lang, 'reg_login')} required icon="fa-user-lock"
              value={form.login} onChange={setField('login')}
              error={fieldError('login')} autoComplete="username" maxLength={REG_MAX_LEN.login}
              hint={t(lang, 'reg_login_hint')} />
            {/* Жива підказка про зайнятість логіна — не залежить від touched
                (на відміну від fieldError вище), показується одразу під час
                набору. Якщо саме поле вже підсвічено помилкою (напр. порожнє) —
                підказку не дублюємо. */}
            {!fieldError('login') && loginCheck.status === 'checking' && (
              <div className="reg-login-status reg-login-status--checking">
                <Spinner animation="border" size="sm" /> {t(lang, 'reg_login_checking')}
              </div>
            )}
            {!fieldError('login') && loginCheck.status === 'available' && loginCheck.checkedLogin === form.login.trim() && (
              <div className="reg-login-status reg-login-status--available">
                <i className="fa-solid fa-circle-check" /> {t(lang, 'reg_login_available')}
              </div>
            )}
            {!fieldError('login') && loginCheck.status === 'taken' && loginCheck.checkedLogin === form.login.trim() && (
              <div className="reg-login-status reg-login-status--taken">
                <i className="fa-solid fa-circle-xmark" /> {t(lang, 'reg_login_taken_hint')}
              </div>
            )}
          </div>
          <RegField id="reg-password" label={t(lang, 'reg_password')} required type="password" icon="fa-lock"
            value={form.password} onChange={setField('password')}
            error={fieldError('password')} autoComplete="new-password" maxLength={REG_MAX_LEN.password}
            hint={t(lang, 'reg_password_hint')} />
        </div>
        <div className="reg-row">
          <RegField id="reg-password-confirm" label={t(lang, 'reg_password_confirm')} required type="password" icon="fa-lock"
            value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
            error={fieldError('confirmPassword')} autoComplete="new-password" maxLength={REG_MAX_LEN.password} />
        </div>
      </div>

      {/* Перевірка, що ви не робот (сесія 2026-10-27) — картинка-капча з
          нашого сервера (reg_antibot.php), без сторонніх сервісів. */}
      <div className="reg-section">
        <div className="reg-section-title">{t(lang, 'reg_section_captcha')}</div>
        {/* reg-captcha-row — картинка+оновлення і поле "Цифри з картинки":
            у вузькій картці в стовпчик, на ≥1200px — в один рядок */}
        <div className="reg-captcha-row">
        <div className="reg-captcha-box">
          {captcha && !captchaLoading
            ? <img className="reg-captcha-img" src={captcha.image} alt={t(lang, 'reg_captcha_alt')} />
            : <div className="reg-captcha-img reg-captcha-img--loading"><Spinner animation="border" size="sm" /></div>}
          <button type="button" className="reg-captcha-refresh" onClick={refreshCaptcha}
            disabled={captchaLoading || submitting}
            aria-label={t(lang, 'reg_captcha_refresh')} title={t(lang, 'reg_captcha_refresh')}>
            <i className="fa-solid fa-rotate" />
          </button>
        </div>
        <div className="reg-row">
          <RegField id="reg-captcha" label={t(lang, 'reg_captcha_label')} required icon="fa-shield-halved"
            value={captchaValue} inputMode="numeric"
            onChange={e => setCaptchaValue(e.target.value.replace(/\D/g, '').slice(0, 5))}
            error={captchaError || fieldError('captcha')} />
        </div>
        </div>
        {/* Поле-пастка: людина його не бачить і не заповнює, боти — заповнюють. */}
        <div className="reg-hp" aria-hidden="true">
          <input type="text" name="reg_website" tabIndex={-1} autoComplete="off"
            value={hpValue} onChange={e => setHpValue(e.target.value)} />
        </div>
      </div>

      {/* Кнопка відправки форми — reg-btn-main (той самий клас і на екрані "Заявку
          прийнято" вище, там він виконує роль кнопки "Повернутися до входу") */}
      {otpPaused && !otpOpen && (
        <button type="button" className="reg-btn reg-btn-login reg-otp-reopen-btn" onClick={reopenOtpModal}>
          <i className="fa-solid fa-mobile-screen-button" /> {t(lang, 'reg_otp_reopen_btn')}
        </button>
      )}
<button type="submit" className="reg-btn reg-btn-main" disabled={submitting}>
        {submitting
          ? <><Spinner size="sm" animation="border" className="me-2" />{t(lang, checkingBot ? 'reg_checking_bot' : 'reg_submitting')}</>
          : <><i className="fa-solid fa-paper-plane" /> {t(lang, 'reg_submit_btn')}</>
        }
      </button>
      </div>

      {otpOpen && createPortal(
        // Клік по фону навмисно НЕ закриває модалку (крок 3 плану 2026-10-26):
        // на телефоні випадковий дотик повз вікно губив заявку. Закриття — лише хрестиком.
        <div className="reg-otp-modal-backdrop">
          <div className="reg-otp-modal" onClick={e => e.stopPropagation()}>
            <div className="reg-otp-modal-header">
              <span>
                {otpStage === 'candidates' ? t(lang, 'reg_candidates_title')
                  : otpStage === 'alreadyRegisteredConfirm' ? t(lang, 'reg_already_registered_confirm_title')
                  : otpStage === 'retry' ? t(lang, 'reg_retry_title')
                  : t(lang, 'reg_otp_title')}
              </span>
              <button type="button" className="reg-otp-modal-close" onClick={closeOtpModal} aria-label={t(lang, 'reg_otp_cancel_btn')}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="reg-otp-modal-body">
              {otpStage === 'code' && (
                <form onSubmit={handleOtpSubmit}>
                  <p className="reg-otp-subtitle">{t(lang, 'reg_otp_subtitle')} <strong>{form.phone}</strong></p>

                  {otpDebugCode && (
                    <div className="reg-otp-debug">
                      <i className="fa-solid fa-flask" />
                      <div>
                        <div className="reg-otp-debug-label">{t(lang, 'reg_otp_debug_label')}</div>
                        <div className="reg-otp-debug-code">{otpDebugCode}</div>
                      </div>
                    </div>
                  )}

                  <label className="reg-label" htmlFor="reg-otp-input">{t(lang, 'reg_otp_input_label')}</label>
                  <input
                    id="reg-otp-input"
                    className="reg-otp-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpValue}
                    onChange={e => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                  />
                  <div className="reg-otp-ttl-hint">{t(lang, 'reg_otp_ttl_hint')}</div>

                  {otpError && <div className="reg-otp-error"><i className="fa-solid fa-circle-exclamation" /> {otpError}</div>}

                  <button type="submit" className="reg-btn reg-btn-main" disabled={otpSubmitting || otpValue.length !== 6}>
                    {otpSubmitting
                      ? <><Spinner size="sm" animation="border" className="me-2" />{t(lang, 'reg_otp_submitting')}</>
                      : t(lang, 'reg_otp_submit_btn')}
                  </button>
                  <button
                    type="button"
                    className="reg-otp-resend-btn"
                    onClick={handleResendOtp}
                    disabled={otpResends >= 5 || resendWait > 0}
                  >
                    {otpResends >= 5
                      ? t(lang, 'reg_otp_resend_limit')
                      : resendWait > 0
                        ? `${t(lang, 'reg_otp_resend_btn')} (${Math.floor(resendWait / 60)}:${String(resendWait % 60).padStart(2, '0')})`
                        : t(lang, 'reg_otp_resend_btn')}
                  </button>
                </form>
              )}

              {otpStage === 'alreadyRegisteredConfirm' && (
                <div>
                  <p className="reg-otp-subtitle">{t(lang, 'reg_already_registered_confirm_text')}</p>
                  <div className="reg-candidates-list">
                    <button
                      type="button"
                      className="reg-btn reg-btn-main"
                      disabled={otpSubmitting}
                      onClick={() => handleConfirmAlreadyRegistered(true)}
                    >
                      {t(lang, 'reg_already_registered_yes_btn')}
                    </button>
                    <button
                      type="button"
                      className="reg-btn reg-btn-main reg-candidates-none-btn"
                      disabled={otpSubmitting}
                      onClick={() => handleConfirmAlreadyRegistered(false)}
                    >
                      {t(lang, 'reg_already_registered_no_btn')}
                    </button>
                  </div>
                </div>
              )}

              {otpStage === 'candidates' && (
                <div>
                  <p className="reg-otp-subtitle">{t(lang, 'reg_candidates_subtitle')}</p>
                  <div className="reg-candidates-list">
                    {candidates.map(c => (
                      <div className="reg-candidate-row" key={c.mfn}>
                        <div className="reg-candidate-info">
                          <div><span className="reg-candidate-label">{t(lang, 'reg_candidate_faculty')}:</span> {c.faculty || '—'}</div>
                          <div><span className="reg-candidate-label">{t(lang, 'reg_candidate_program')}:</span> {c.program || '—'}</div>
                          {c.group && (
                            <div><span className="reg-candidate-label">{t(lang, 'reg_candidate_group')}:</span> {c.group}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="reg-candidate-pick-btn"
                          disabled={otpSubmitting}
                          onClick={() => handleSelectCandidate(c.mfn)}
                        >
                          {t(lang, 'reg_candidates_pick_btn')}
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="reg-btn reg-btn-main reg-candidates-none-btn"
                    disabled={otpSubmitting}
                    onClick={() => handleSelectCandidate(0)}
                  >
                    {t(lang, 'reg_candidates_none_btn')}
                  </button>
                </div>
              )}

              {/* Екран повтору фінального запису (крок 6 плану 2026-10-26) */}
              {otpStage === 'retry' && (
                <div>
                  <p className="reg-otp-subtitle">
                    {retryError === 'login_taken' ? t(lang, 'reg_retry_login_taken_text') : t(lang, 'reg_retry_failed_text')}
                  </p>
                  {retryError === 'login_taken' && (
                    <RegField id="reg-retry-login" label={t(lang, 'reg_login')} required icon="fa-user-lock"
                      value={retryLogin} onChange={e => setRetryLogin(e.target.value)}
                      error={retryLogin.trim() && !isLoginValid(retryLogin.trim()) ? t(lang, 'reg_err_login_format') : ''}
                      hint={t(lang, 'reg_login_hint')} autoComplete="username" maxLength={REG_MAX_LEN.login} />
                  )}
                  <button
                    type="button"
                    className="reg-btn reg-btn-main"
                    disabled={otpSubmitting || (retryError === 'login_taken' && !isLoginValid(retryLogin.trim()))}
                    onClick={handleRetryFinal}
                  >
                    {otpSubmitting
                      ? <><Spinner size="sm" animation="border" className="me-2" />{t(lang, 'reg_otp_submitting')}</>
                      : t(lang, retryError === 'login_taken' ? 'reg_retry_login_btn' : 'reg_retry_btn')}
                  </button>
                </div>
              )}

              {/* Помилки на екранах, крім введення коду (там свій блок усередині форми) */}
              {otpStage !== 'code' && otpError && (
                <div className="reg-otp-error"><i className="fa-solid fa-circle-exclamation" /> {otpError}</div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </form>
  );
}

export default RegisterForm;